import React, { useEffect, Suspense, lazy } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import Footer from "./Components/Footer";
import Header from "./Components/Header";
import { normalizePathname } from "./utils/pathname";
import useMobileView from "./hooks/useMobileView";

import NatureTouch from "./Components/NatureTouch";

// Load secondary pages only when visited.
const VideoList = lazy(() => import("./Components/YoutubeVideos/VideoContent"));
const BlogList = lazy(() => import("./Components/Blog/BlogList"));
const BlogArticle = lazy(() => import("./Components/Blog/BlogArticle"));
const Pricing = lazy(() => import("./Components/Pricing/Pricing"));
const Billing = lazy(() => import("./Components/Billing/Billing"));
const PrivacyPolicy = lazy(() => import("./Components/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./Components/TermsAndConditions"));
const ContactUs = lazy(() => import("./Components/ContactUs"));
const AboutUs = lazy(() => import("./Components/AboutUs"));
const RefundPolicy = lazy(() => import("./Components/RefundPolicy"));
const NotFound = lazy(() => import("./Components/NotFound"));

// ✅ Scroll-to-top logic
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.hash]);

  return null;
}

// 🔄 Loading spinner for lazy components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
  </div>
);

const App = () => {
  const location = useLocation();
  const mobile = useMobileView();
  const isMixer = ["/", "/playlist", "/saved", "/gallery", "/live-gallery"].includes(normalizePathname(location.pathname));
  const isLiveGallery = !mobile && normalizePathname(location.pathname) === "/live-gallery";
  return (
    <div className={`App flex flex-col min-h-screen text-white ${isLiveGallery ? "live-gallery-page" : ""} ${isMixer ? "mixer-theme" : "bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500"}`}>
      {/* Sticky or responsive header if needed */}
      <Header />

      {/* Main content area */}
      <main className="flex-grow w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 z-0">
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<NatureTouch />} />
            <Route path="/playlist" element={<NatureTouch />} />
            <Route path="/saved" element={<NatureTouch />} />
            <Route path="/gallery" element={<NatureTouch />} />
            <Route path="/live-gallery" element={mobile ? <Navigate to="/gallery" replace /> : <NatureTouch />} />
            <Route path="/videos" element={<VideoList />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/billing" element={<Billing />} />

            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default App;
